import { MAINLINES } from "./bookCoaching.ts";
import { manifestByOpeningId } from "../content/manifest.ts";

/**
 * The name of the opening the moves have reached, from the Black-defence
 * list and, when that list is still only a family, from another course's
 * main line. Empty when that name is already the card's title.
 */
export type OpeningIdentity = {
  name: string;
  /** 0 is a family such as the Open Game. 1 and 2 are a named defence or variation. */
  specificity: number;
};

type IdentifyResult = {
  primary: { entry: { name: string; specificity: number }; confirmed: boolean } | null;
};

type IdentifyFn = (
  history: Array<{ ply: number; san: string }>,
  whiteFirstMove: string | null
) => IdentifyResult;

let identify: IdentifyFn | null = null;
let loading: Promise<void> | null = null;

/** Download the openings list with the game, not with the opening picker. */
export function loadOpponentCategories(): Promise<void> {
  if (identify) return Promise.resolve();
  if (!loading) {
    loading = import("@js/identifyBlackOpening.js")
      .then((mod) => {
        identify = mod.identifyBlackOpening as IdentifyFn;
      })
      .catch((error) => {
        loading = null;
        throw error;
      });
  }
  return loading;
}

function bare(san: string): string {
  return san.replace(/[+#?!]/g, "");
}

function core(name: string): string {
  return name
    .replace(/Defence/g, "Defense")
    .replace(/\s+\([^)]*\)$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function sameName(a: string, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return core(a) === core(b);
}

function displayName(name: string): string {
  return name
    .replace(/Defence/g, "Defense")
    .replace(/\s+\((?!\d)[^)]*\)$/, "")
    .trim();
}

/** A confirmed entry from the openings list. Nothing is guessed from a partial match. */
export function confirmedOpening(sans: string[]): OpeningIdentity | null {
  if (!identify || sans.length < 2) return null;
  const history = sans.map((san, index) => ({ ply: index + 1, san: bare(san) }));
  let result: IdentifyResult;
  try {
    result = identify(history, bare(sans[0] || "") || null);
  } catch {
    return null;
  }
  const primary = result.primary;
  if (!primary?.confirmed || !primary.entry.name) return null;
  return { name: primary.entry.name, specificity: primary.entry.specificity };
}

/**
 * Another course's main line, when the moves so far are that line and no
 * second course fits them equally well. The course already being played is
 * not news.
 */
export function otherCourseTitle(sans: string[], currentOpeningId: string | null): string | null {
  const played = sans.map(bare);
  if (played.length < 5) return null;
  const matches: string[] = [];
  for (const [id, line] of Object.entries(MAINLINES)) {
    if (!line || id === currentOpeningId) continue;
    let matched = 0;
    while (
      matched < played.length &&
      matched < line.length &&
      played[matched] === bare(line[matched]!)
    ) {
      matched += 1;
    }
    if (matched === played.length) matches.push(id);
  }
  if (matches.length !== 1) return null;
  return manifestByOpeningId(matches[0]!)?.title || null;
}

/**
 * "This is the X." for the opening the moves have reached. Empty when the
 * name is already on the card or is the course being played: a Caro-Kann
 * course never announces the Caro-Kann.
 */
export function opponentCategory(
  sans: string[],
  shownName: string | null | Array<string | null | undefined>,
  currentOpeningId: string | null
): string {
  const opening = confirmedOpening(sans);
  const course = otherCourseTitle(sans, currentOpeningId);
  let chosen: string | null = null;
  if (opening && opening.specificity >= 1) chosen = displayName(opening.name);
  else if (course && (!opening || !sameName(course, opening.name))) chosen = course;
  else if (opening) chosen = displayName(opening.name);
  if (!chosen) return "";
  const shown = Array.isArray(shownName) ? shownName : [shownName];
  if (shown.some((name) => sameName(chosen!, name))) return "";
  return `This is the ${chosen}.`;
}
